import { Box, Stack, Typography, lighten, styled } from "@mui/material"
import {
  FieldSection,
  FieldSectionTitle,
  FormContainer,
  FormDateField,
  FormNumberField,
  FormRoleConfContext,
  FormSearchField,
  FormSwitchField,
  useRoleConf,
} from "@pharmupp/p3-front-commons"
import { Check } from "react-feather"
import { useFormContext } from "react-hook-form"
import { formRoleConf } from "./Assignment.formRoleConf"
import { AssignmentEnum, type AssignmentFormValues } from "./Assignment.schema"
import { useAssignementReferential } from "./useAssignementReferential"

export function Assignment() {
  const conf = useRoleConf(formRoleConf)
  const { referential } = useAssignementReferential()

  return (
    <FormRoleConfContext.Provider value={conf}>
      <FormContainer>
        <FieldSectionTitle title="Affectation" />
        <FieldSection
          title="UPP de rattachement"
          subtitle="Choisir l’UPP de rattachement dans la liste"
        >
          <FormSearchField<number, AssignmentFormValues>
            name={AssignmentEnum.UPP_ID}
            label="UPP"
            options={referential.upps}
            valueFormat="optionValue"
            required
          />
        </FieldSection>

        <FieldSection title="Secteur" subtitle="Choisir le secteur dans la liste">
          <FormSearchField<number, AssignmentFormValues>
            name={AssignmentEnum.SECTOR_ID}
            label="Secteur"
            options={referential.sectors}
            valueFormat="optionValue"
            required
          />
        </FieldSection>

        <FieldSection
          title="Adhésion Ultra"
          subtitle="Préciser si la pharmacie est de type “Ultra”"
        >
          <Stack direction="row" alignItems="center">
            <FormSwitchField<AssignmentFormValues>
              name={AssignmentEnum.ULTRA}
              toggleLabels={["Non", "Oui"]}
            />
            <ChipUltra name={AssignmentEnum.ULTRA} />
          </Stack>
        </FieldSection>

        <FieldSection
          name="negativeSegmentationSection"
          title="Segmentation négative"
          subtitle="Préciser si la pharmacie est en segmentation négative"
        >
          <FormSwitchField<AssignmentFormValues>
            name={AssignmentEnum.NEGATIVE_SEGMENTATION}
            toggleLabels={["Non", "Oui, pharmacie en segmentation négative"]}
          />
        </FieldSection>

        <FieldSection
          title="Ancienneté"
          subtitle="Si le titulaire a racheté une officine ou que la pharmacie a repris une officine adhérente au groupement"
        >
          <FormDateField<AssignmentFormValues>
            label="Date d’entrée"
            name={AssignmentEnum.ENTRY_DATE}
            required
          />
        </FieldSection>

        <FieldSection title="Code CERP" subtitle="Code client CERP">
          <FormNumberField<AssignmentFormValues>
            format="#####"
            name={AssignmentEnum.CERP_CODE}
            label="Code client CERP"
            fullWidth
            required
          />
        </FieldSection>

        <FieldSection
          title="Établissement CERP"
          subtitle="Sélectionner un établissement CERP"
          name="cerpBranchSection"
        >
          <FormSearchField<string, AssignmentFormValues>
            name={AssignmentEnum.CERP_BRANCH}
            label="Établissement CERP"
            options={referential.cerpBranch}
            valueFormat="optionObject"
            fullWidth
            required
          />
        </FieldSection>
      </FormContainer>
    </FormRoleConfContext.Provider>
  )
}

const ChipUltra = ({ name }: { name: AssignmentEnum }) => {
  const formContext = useFormContext()
  const isUltra = formContext.watch(name)

  if (!isUltra) return null

  return (
    <ChipContainer>
      <ChipUltraStyled>
        <Check size={10} strokeWidth={3} />
        <Typography fontSize={10} fontWeight={600}>
          ULTRA
        </Typography>
      </ChipUltraStyled>
    </ChipContainer>
  )
}

const ChipUltraStyled = styled(Stack)(
  ({
    theme: {
      palette: { common },
      spacing,
    },
  }) => ({
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing(0.5),
    height: "min-content",
    color: common.electricBlue,
    padding: spacing(0, 0.5, 0),
    borderRadius: "200px",
    border: `1px solid ${lighten(common.electricBlue, 0.75)}`,
  }),
)

const ChipContainer = styled(Box)(({ theme: { spacing, palette } }) => ({
  height: "min-content",
  paddingLeft: spacing(2),
  borderLeft: `1px solid ${palette.divider}`,
}))
